import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Edit,
  MapPin,
  Trash2,
  Upload,
  Search,
  Sparkles,
  Loader2,
  AlertTriangle,
  X,
  GitCompare,
  Lock,
  Unlock,
  Trophy,
  Linkedin,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import CandidateModal from "@/components/CandidateModal";
import CandidateCard from "@/components/CandidateCard";
import CandidateComparisonModal from "@/components/CandidateComparisonModal";
import JobShareAndSearchPanel from "@/components/JobShareAndSearchPanel";
import SourceBreakdownPanel from "@/components/SourceBreakdownPanel";
import JobInterviewsPanel from "@/components/JobInterviewsPanel";
import JobClosureReport from "@/components/JobClosureReport";
import JobKanban from "@/components/JobKanban";
import HireCandidateDialog from "@/components/HireCandidateDialog";
import JobLinkedInPublishDialog from "@/components/JobLinkedInPublishDialog";
import JobLinkedInProspectsPanel from "@/components/JobLinkedInProspectsPanel";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const MAX_COMPARE = 5;

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [orgSlug, setOrgSlug] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteAllCandidates, setConfirmDeleteAllCandidates] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closingBusy, setClosingBusy] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [linkedinDialogOpen, setLinkedinDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [unpublishBusy, setUnpublishBusy] = useState(false);
  // Candidato selecionado para formalizar contratação a partir do diálogo de encerramento.
  const [hireFromCloseCandidate, setHireFromCloseCandidate] = useState(null);

  // ── Compare candidates mode ──────────────────────────────────────
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]); // candidate ids in selection order
  const [comparisonOpen, setComparisonOpen] = useState(false);

  // Tab title: job name + candidate count when both are available
  useDocumentTitle(
    job
      ? `${job.title}${candidates.length ? ` · ${candidates.length} candidato${candidates.length === 1 ? "" : "s"}` : ""}`
      : "Vaga",
    { ready: !loading },
  );

  const handleToggleCompareMode = () => {
    setCompareMode((prev) => {
      const next = !prev;
      if (!next) setSelectedForCompare([]);
      return next;
    });
  };

  const handleToggleSelect = (cand) => {
    if (cand.status === "pendente" || cand.status === "erro") {
      toast.error(
        "Apenas candidatos já analisados podem ser comparados."
      );
      return;
    }
    setSelectedForCompare((prev) => {
      if (prev.includes(cand.id)) {
        return prev.filter((id) => id !== cand.id);
      }
      if (prev.length >= MAX_COMPARE) {
        toast.info(`Você pode comparar no máximo ${MAX_COMPARE} candidatos por vez.`);
        return prev;
      }
      return [...prev, cand.id];
    });
  };

  const handleOpenComparison = () => {
    if (selectedForCompare.length < 2) {
      toast.error("Selecione pelo menos 2 candidatos para comparar.");
      return;
    }
    setComparisonOpen(true);
  };

  const handleReanalyzeAll = async () => {
    setReanalyzing(true);
    try {
      const res = await axios.post(`${API}/jobs/${id}/candidates/reanalyze`);
      setCandidates(res.data);
      toast.success(`${res.data.length} candidato(s) re-analisado(s)`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao reanalisar candidatos"
      );
    } finally {
      setReanalyzing(false);
    }
  };

  const handleReanalyzeCandidate = async (c) => {
    try {
      // Optimistically flip to "pendente" so the UI shows the analysing state.
      setCandidates((prev) =>
        prev.map((x) =>
          x.id === c.id ? { ...x, status: "pendente", summary: "" } : x
        )
      );
      await axios.post(`${API}/candidates/${c.id}/reanalyze`);
      toast.success("Reanálise iniciada — atualizando…");
      // Refresh after a couple seconds to pick up the new analysis state.
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/jobs/${id}/candidates`);
          setCandidates(res.data);
        } catch (err) {
          console.warn("Failed to refresh candidates after reanalyze:", err);
        }
      }, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reanalisar");
      // Rollback optimistic update
      setCandidates((prev) =>
        prev.map((x) => (x.id === c.id ? c : x))
      );
    }
  };

  const handleDeleteCandidate = async (c) => {
    try {
      await axios.delete(`${API}/candidates/${c.id}`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Candidato excluído");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleDeleteAllCandidates = async () => {
    setDeletingAll(true);
    try {
      const res = await axios.delete(`${API}/jobs/${id}/candidates`);
      setCandidates([]);
      toast.success(res.data.message || "Candidatos excluídos");
      setConfirmDeleteAllCandidates(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir candidatos");
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteFailedCandidates = async () => {
    try {
      const res = await axios.delete(`${API}/jobs/${id}/candidates/failed`);
      toast.success(res.data.message || "Candidatos com falha excluídos");
      await refreshAll();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao excluir candidatos com falha"
      );
    }
  };

  const refreshAll = async () => {
    try {
      const [j, c] = await Promise.all([
        axios.get(`${API}/jobs/${id}`),
        axios.get(`${API}/jobs/${id}/candidates`),
      ]);
      setJob(j.data);
      setCandidates(c.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Vaga não encontrada");
        navigate("/vagas");
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    refreshAll().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Whenever the job's organization_id changes, resolve its slug so the
  // public share link points to /c/org/{org-slug}/{job-slug}.
  useEffect(() => {
    if (!job?.organization_id) {
      setOrgSlug("");
      return;
    }
    axios
      .get(`${API}/registry/organizations`)
      .then((res) => {
        const match = (res.data || []).find((o) => o.id === job.organization_id);
        setOrgSlug(match?.slug || "");
      })
      .catch(() => setOrgSlug(""));
  }, [job?.organization_id]);

  // Auto-poll while any candidate is still being processed in background
  const hasPending = candidates.some((c) => c.status === "pendente");
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => {
      axios
        .get(`${API}/jobs/${id}/candidates`)
        .then((res) => setCandidates(res.data))
        .catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [hasPending, id]);

  const filtered = useMemo(() => {
    if (!search.trim()) return candidates;
    const s = search.toLowerCase();
    return candidates.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.skills || []).some((k) => k.toLowerCase().includes(s))
    );
  }, [search, candidates]);

  const pendingCount = candidates.filter((c) => c.status === "pendente").length;
  const allZeroScores =
    candidates.length > 0 &&
    !hasPending &&
    candidates.every((c) => c.score === 0 && !c.email && !c.phone);
  const allEmptyText =
    candidates.length > 0 && candidates.every((c) => !c.resume_text);
  const failedCount = candidates.filter(
    (c) =>
      c.status === "erro" ||
      (c.status !== "pendente" && !c.resume_text)
  ).length;
  const hasMixedFailures =
    !hasPending && failedCount > 0 && failedCount < candidates.length;

  // Candidatos aprovados que ainda não foram formalmente contratados.
  // Usado pra alertar no diálogo de "Encerrar vaga": sem essa formalização,
  // o relatório de fechamento mostra 0 contratações.
  const approvedCandidates = useMemo(
    () => candidates.filter((c) => c.status === "aprovado"),
    [candidates],
  );
  const hasHiredCandidate = useMemo(
    () => candidates.some((c) => c.status === "contratado"),
    [candidates],
  );

  const handleDeleteJob = async () => {
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast.success("Vaga excluída");
      navigate("/vagas");
    } catch {
      toast.error("Erro ao excluir vaga");
    }
  };

  const handleCloseJob = async () => {
    setClosingBusy(true);
    try {
      const res = await axios.post(`${API}/jobs/${id}/close`, {});
      setJob(res.data);
      toast.success("Vaga encerrada. Lembretes e mensagens automáticas pausados.");
      setConfirmClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao encerrar vaga");
    } finally {
      setClosingBusy(false);
    }
  };

  const handleUnpublishLinkedIn = async () => {
    setUnpublishBusy(true);
    try {
      const res = await axios.delete(`${API}/jobs/${id}/linkedin/post`);
      toast.success(res.data?.message || "Publicação removida.");
      setUnpublishDialogOpen(false);
      await refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao despublicar");
    } finally {
      setUnpublishBusy(false);
    }
  };

  const handleReopenJob = async () => {
    setClosingBusy(true);
    try {
      const res = await axios.post(`${API}/jobs/${id}/reopen`, {});
      setJob(res.data);
      toast.success("Vaga reaberta.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reabrir vaga");
    } finally {
      setClosingBusy(false);
    }
  };

  const onCandidateUpdated = (updated) => {
    // Defensive guard: callers MUST pass the updated candidate doc, but we
    // never want a missing/undefined arg to crash the whole modal (white
    // screen). Bail out gracefully if no payload arrived.
    if (!updated || !updated.id) {
      return;
    }
    setCandidates((list) =>
      list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
    );
    setActiveCandidate((c) => (c?.id === updated.id ? { ...c, ...updated } : c));
  };

  const onCandidateDeleted = (cid) => {
    setCandidates((list) => list.filter((c) => c.id !== cid));
    setActiveCandidate(null);
  };

  if (loading || !job) {
    return <div className="text-sm text-slate-500">Carregando...</div>;
  }

  return (
    <div data-testid="job-detail-page" className="space-y-8">
      <Link
        to="/vagas"
        data-testid="back-to-jobs"
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar para vagas
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2">
              <StatusBadge status={job.status} />
              {job.job_code && (
                <span className="text-xs font-mono font-semibold text-slate-500">
                  {job.job_code}
                </span>
              )}
              <span className="text-xs text-slate-500">{job.candidates_count} candidatos</span>
            </div>
            <h1 className="heading text-3xl font-bold text-slate-950 leading-tight">
              {job.title}
            </h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
              {job.department && <span>{job.department}</span>}
              {job.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {job.location}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Briefcase className="w-3.5 h-3.5" />
                {job.employment_type}
              </span>
              {job.experience_years > 0 && (
                <span>{job.experience_years}+ anos de experiência</span>
              )}
              {job.salary_range && <span>{job.salary_range}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {job.status === "encerrada" ? (
              <Button
                variant="outline"
                data-testid="reopen-job-button"
                onClick={handleReopenJob}
                disabled={closingBusy}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              >
                {closingBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                ) : (
                  <Unlock className="w-4 h-4 mr-1.5" />
                )}
                Reabrir vaga
              </Button>
            ) : (
              <Button
                variant="outline"
                data-testid="close-job-button"
                onClick={() => setConfirmClose(true)}
                disabled={closingBusy}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              >
                <Lock className="w-4 h-4 mr-1.5" />
                Encerrar vaga
              </Button>
            )}
            <Link
              to={`/vagas/${id}/upload`}
              data-testid="upload-resumes-button"
              className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload de currículos
            </Link>
            {/* Publicar / Agendada / Já publicada no LinkedIn */}
            {job?.linkedin_post_id ? (
              <div className="inline-flex rounded-md border border-blue-200 overflow-hidden">
                <a
                  href={job.linkedin_post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-linkedin-post-link"
                  className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2.5 text-sm font-medium transition-colors border-r border-blue-200"
                  title="Abrir o post no LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                  Publicada
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={() => setUnpublishDialogOpen(true)}
                  data-testid="unpublish-linkedin-button"
                  className="px-2 py-2.5 bg-blue-50 hover:bg-rose-50 text-blue-700 hover:text-rose-700 transition-colors"
                  title="Despublicar do LinkedIn"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : job?.linkedin_schedule_status === "pending" ? (
              <Button
                onClick={() => setLinkedinDialogOpen(true)}
                data-testid="linkedin-scheduled-badge"
                className="bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 px-4 py-2.5"
                title={`Publicação agendada — clique pra reagendar ou cancelar`}
                variant="outline"
              >
                <Linkedin className="w-4 h-4 mr-1.5" />
                Agendada
              </Button>
            ) : (
              <Button
                onClick={() => setLinkedinDialogOpen(true)}
                data-testid="publish-linkedin-button"
                disabled={job?.status === "encerrada"}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5"
                title={
                  job?.status === "encerrada"
                    ? "Reabra a vaga pra publicar no LinkedIn"
                    : "Publicar esta vaga no LinkedIn"
                }
              >
                <Linkedin className="w-4 h-4 mr-1.5" />
                Publicar no LinkedIn
              </Button>
            )}
            <Link
              to={`/vagas/${id}/editar`}
              data-testid="edit-job-button"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
            >
              <Edit className="w-4 h-4" />
              Editar
            </Link>
            <Button
              variant="ghost"
              data-testid="delete-job-button"
              onClick={() => setConfirmDelete(true)}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 px-3 py-2.5"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Banner: vaga encerrada */}
        {job.status === "encerrada" && (
          <>
            <div
              data-testid="job-closed-banner"
              className="mt-4 bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2.5"
            >
              <Lock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm text-amber-900">
                <strong>Vaga encerrada.</strong> Os lembretes automáticos de
                entrevista, e-mails de rejeição em massa e alertas da Maya
                estão <strong>pausados</strong> para esta vaga.
                {job.closed_at && (
                  <span className="text-amber-700 text-xs block mt-0.5">
                    Encerrada em{" "}
                    {new Date(job.closed_at).toLocaleString("pt-BR", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                    {job.closed_by_user_name && ` por ${job.closed_by_user_name}`}.
                  </span>
                )}
              </div>
            </div>

            {/* Relatório de fechamento — métricas do processo */}
            <div className="mt-4">
              <JobClosureReport jobId={id} />
            </div>
          </>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
              Descrição
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {job.description}
            </p>
          </div>
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
                Requisitos
              </div>
              {job.requirements.length === 0 ? (
                <div className="text-sm text-slate-400">Nenhum requisito definido.</div>
              ) : (
                <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-4">
                  {job.requirements.map((r, i) => (
                    <li key={`req-${r.slice(0, 30)}-${i}`}>{r}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
                Habilidades
              </div>
              {job.skills.length === 0 ? (
                <div className="text-sm text-slate-400">Nenhuma habilidade definida.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {job.skills.map((s, i) => (
                    <span
                      key={`${s}-${i}`}
                      className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-md border border-slate-200"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Kanban board — candidates grouped by current status */}
      <JobKanban
        jobId={id}
        onCandidateClick={(c) => setActiveCandidate(c)}
        refreshSignal={candidates.length}
      />

      {/* LinkedIn Prospects (auto-sourced via Synkly) */}
      {job && <JobLinkedInProspectsPanel job={job} />}
      {/* Share & Search panel */}
      <JobShareAndSearchPanel
        job={job}
        companySlug={user?.company_slug}
        orgSlug={orgSlug}
        onJobUpdated={(updated) => setJob(updated)}
      />

      {/* Origem dos candidatos desta vaga */}
      <SourceBreakdownPanel
        candidates={candidates}
        title="Origem dos candidatos desta vaga"
        description="Como cada candidato chegou nesta posição"
        emptyHint="Os candidatos desta vaga ainda não foram cadastrados. Faça upload de currículos ou compartilhe o link público pra começar a receber candidaturas."
      />

      {/* Entrevistas agendadas */}
      <JobInterviewsPanel jobId={job.id} job={job} />

      {/* Pending analysis banner */}
      {hasPending && (
        <div
          data-testid="pending-banner"
          className="bg-blue-50 border border-blue-200 rounded-lg p-5 flex items-start gap-3"
        >
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">
              Analisando {pendingCount} de {candidates.length} candidato(s) com IA...
            </div>
            <p className="text-sm text-blue-800 mt-1 leading-relaxed">
              A IA está lendo cada PDF, identificando contatos e calculando o score de
              compatibilidade. A página atualiza automaticamente a cada poucos segundos.
              Você pode navegar livremente — o processamento continua no servidor.
            </p>
          </div>
        </div>
      )}

      {/* Mixed failures warning (some succeeded, some failed) */}
      {hasMixedFailures && (
        <div
          data-testid="mixed-failures-banner"
          className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              {failedCount} de {candidates.length} candidato(s) ficaram sem análise
            </div>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">
              Provavelmente o OCR estava indisponível durante o upload. Clique em
              <strong> "Reanalisar"</strong> abaixo — vamos tentar reprocessar com base
              no PDF original armazenado. Se mesmo assim não funcionar, use{" "}
              <strong>"Excluir falhas"</strong> para limpar e fazer upload novamente.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                data-testid="banner-reanalyze-button"
                onClick={handleReanalyzeAll}
                disabled={reanalyzing}
                size="sm"
                variant="outline"
                className="bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                {reanalyzing ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                )}
                Reanalisar todos
              </Button>
              <Button
                data-testid="banner-delete-failed-button"
                onClick={handleDeleteFailedCandidates}
                size="sm"
                variant="outline"
                className="bg-white border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Excluir falhas ({failedCount})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* All-zero warning banner */}
      {allZeroScores && (
        <div
          data-testid="all-zero-banner"
          className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              Nenhum candidato foi analisado corretamente
            </div>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">
              {allEmptyText ? (
                <>
                  Os PDFs enviados parecem ser <strong>imagens digitalizadas</strong> (sem texto pesquisável).
                  Vou tentar reanalisar com OCR a partir dos PDFs salvos. Se não funcionar,
                  exclua e faça upload novamente.
                </>
              ) : (
                <>
                  A análise pode ter falhado. Clique em "Reanalisar todos" para tentar novamente
                  com a IA, ou faça upload novo dos currículos.
                </>
              )}
            </p>
            <Button
              data-testid="banner-reanalyze-all-button"
              onClick={handleReanalyzeAll}
              disabled={reanalyzing}
              size="sm"
              variant="outline"
              className="mt-3 bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              {reanalyzing ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              )}
              Reanalisar todos
            </Button>
          </div>
        </div>
      )}

      {/* Candidates Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="heading text-lg font-semibold text-slate-900">
              Candidatos ({candidates.length})
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Ordenados pelo score de compatibilidade calculado pela IA.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
            {candidates.length >= 2 && !allEmptyText && (
              <Button
                data-testid="toggle-compare-mode-button"
                onClick={handleToggleCompareMode}
                variant={compareMode ? "default" : "outline"}
                size="sm"
                className={
                  compareMode
                    ? "bg-brand-blue hover:bg-brand-blue-dark text-white shrink-0"
                    : "shrink-0 border-violet-200 text-violet-700 hover:bg-violet-50"
                }
              >
                <GitCompare className="w-3.5 h-3.5 mr-1.5" />
                {compareMode ? "Sair da comparação" : "Comparar candidatos"}
              </Button>
            )}
            {candidates.length > 0 && !allEmptyText && (
              <Button
                data-testid="reanalyze-all-button"
                onClick={handleReanalyzeAll}
                disabled={reanalyzing}
                variant="outline"
                size="sm"
                className="shrink-0"
              >
                {reanalyzing ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                )}
                Reanalisar
              </Button>
            )}
            {candidates.length > 0 && (
              <Button
                data-testid="delete-all-candidates-button"
                onClick={() => setConfirmDeleteAllCandidates(true)}
                variant="outline"
                size="sm"
                className="shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Excluir todos
              </Button>
            )}
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="candidates-search"
                placeholder="Buscar por nome, e-mail ou skill..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {candidates.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-5 h-5 text-slate-600" />
            </div>
            <h3 className="heading text-base font-semibold text-slate-900 mb-1">
              Nenhum currículo enviado ainda
            </h3>
            <p className="text-sm text-slate-600 mb-4 max-w-sm mx-auto">
              Faça upload de PDFs e a IA irá analisar automaticamente cada candidato em comparação
              com esta vaga.
            </p>
            <Link
              to={`/vagas/${id}/upload`}
              data-testid="empty-upload-button"
              className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              Fazer upload
            </Link>
          </div>
        ) : (
          <>
            {compareMode && (
              <div
                data-testid="compare-mode-banner"
                className="px-6 py-3 bg-violet-50 border-b border-violet-200 flex items-center gap-3"
              >
                <GitCompare className="w-4 h-4 text-violet-700 shrink-0" />
                <p className="text-sm text-violet-900 flex-1">
                  <strong>Modo comparação ativo.</strong> Marque de 2 a {MAX_COMPARE} candidatos
                  analisados e clique em <em>Comparar selecionados</em> para gerar a matriz com IA.
                </p>
              </div>
            )}
            <div className="p-4 space-y-4 bg-slate-50/40">
              {filtered.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">
                  Nenhum candidato corresponde à busca.
                </div>
              ) : (
                filtered.map((c, idx) => (
                  <CandidateCard
                    key={c.id}
                    candidate={c}
                    rank={search.trim() ? null : idx + 1}
                    onOpen={setActiveCandidate}
                    onReanalyze={handleReanalyzeCandidate}
                    onDelete={handleDeleteCandidate}
                    selectable={compareMode}
                    selected={selectedForCompare.includes(c.id)}
                    onToggleSelect={handleToggleSelect}
                    selectionDisabled={
                      selectedForCompare.length >= MAX_COMPARE &&
                      !selectedForCompare.includes(c.id)
                    }
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {activeCandidate && (
        <CandidateModal
          candidate={activeCandidate}
          job={job}
          onClose={() => setActiveCandidate(null)}
          onUpdated={onCandidateUpdated}
          onDeleted={onCandidateDeleted}
        />
      )}

      {/* Floating action bar when in compare mode */}
      {compareMode && selectedForCompare.length > 0 && (
        <div
          data-testid="compare-floating-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white shadow-2xl rounded-full px-5 py-3 flex items-center gap-4 border border-slate-800 animate-in slide-in-from-bottom-2"
        >
          <span className="text-sm font-semibold">
            {selectedForCompare.length}{" "}
            {selectedForCompare.length === 1 ? "selecionado" : "selecionados"}
            <span className="text-slate-400 font-normal ml-1">
              / {MAX_COMPARE} máx
            </span>
          </span>
          <button
            data-testid="clear-compare-selection"
            onClick={() => setSelectedForCompare([])}
            className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
            type="button"
          >
            <X className="w-3 h-3" />
            Limpar
          </button>
          <Button
            data-testid="open-comparison-button"
            onClick={handleOpenComparison}
            disabled={selectedForCompare.length < 2}
            size="sm"
            className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Comparar selecionados
            {selectedForCompare.length >= 2 ? ` (${selectedForCompare.length})` : ""}
          </Button>
        </div>
      )}

      {comparisonOpen && (
        <CandidateComparisonModal
          jobId={id}
          candidateIds={selectedForCompare}
          open={comparisonOpen}
          onClose={() => setComparisonOpen(false)}
        />
      )}

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent data-testid="confirm-close-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Encerrar esta vaga?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Ao encerrar a vaga <strong>{job?.title}</strong>:
              </span>
              <ul className="list-disc pl-5 text-sm space-y-1 text-slate-700">
                <li>Lembretes automáticos de entrevista <strong>param de ser enviados</strong></li>
                <li>E-mails automáticos de rejeição em massa <strong>pausam</strong></li>
                <li>A Maya não vai mais alertar sobre candidatos em risco desta vaga</li>
                <li>Entrevistas já confirmadas continuam acontecendo normalmente</li>
              </ul>
              <span className="block text-slate-600 text-sm">
                Você pode reabrir a qualquer momento clicando em <strong>Reabrir vaga</strong>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Atalho: candidatos aprovados sem contratação formalizada */}
          {!hasHiredCandidate && approvedCandidates.length > 0 && (
            <div
              data-testid="approved-without-hire-warning"
              className="mt-2 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-md p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <Trophy className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-amber-900 leading-relaxed">
                  <strong>
                    Você tem {approvedCandidates.length} candidato
                    {approvedCandidates.length > 1 ? "s" : ""} aprovado
                    {approvedCandidates.length > 1 ? "s" : ""} sem contratação formalizada.
                  </strong>{" "}
                  Sem isso, o <strong>relatório de fechamento</strong> vai mostrar{" "}
                  <strong>0 contratações</strong>. Formalize antes de encerrar:
                </div>
              </div>
              <div className="flex flex-col gap-1.5 pl-7">
                {approvedCandidates.slice(0, 5).map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-2 bg-white border border-amber-200 rounded px-2.5 py-1.5"
                  >
                    <div className="text-sm text-slate-800 truncate flex-1 min-w-0">
                      <strong>{c.name || "(sem nome)"}</strong>
                      {typeof c.score === "number" && (
                        <span className="text-xs text-slate-500 ml-1.5">
                          score {c.score}%
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      data-testid={`hire-from-close-${c.id}`}
                      onClick={() => {
                        setConfirmClose(false);
                        setHireFromCloseCandidate(c);
                      }}
                      className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    >
                      <Trophy className="w-3 h-3 mr-1" />
                      Marcar como contratado
                    </Button>
                  </div>
                ))}
                {approvedCandidates.length > 5 && (
                  <div className="text-xs text-amber-700 italic">
                    + {approvedCandidates.length - 5} outro
                    {approvedCandidates.length - 5 > 1 ? "s" : ""} — abra cada
                    candidato no funil pra formalizar.
                  </div>
                )}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-close-job">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-close-job"
              onClick={handleCloseJob}
              disabled={closingBusy}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {closingBusy ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <Lock className="w-4 h-4 mr-1.5" />
              )}
              {!hasHiredCandidate && approvedCandidates.length > 0
                ? "Encerrar mesmo assim"
                : "Encerrar vaga"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Atalho de contratação a partir do diálogo de encerramento */}
      {hireFromCloseCandidate && (
        <HireCandidateDialog
          open={!!hireFromCloseCandidate}
          onClose={() => setHireFromCloseCandidate(null)}
          candidate={hireFromCloseCandidate}
          jobTitle={job?.title || ""}
          onHired={() => {
            // Após contratar, recarrega tudo — o wizard já encerra a vaga
            // automaticamente (closeJob=true por default).
            setHireFromCloseCandidate(null);
            refreshAll();
          }}
        />
      )}

      {/* Publicação no LinkedIn (Fase 2 Unipile) */}
      {linkedinDialogOpen && (
        <JobLinkedInPublishDialog
          open={linkedinDialogOpen}
          onClose={() => setLinkedinDialogOpen(false)}
          job={job}
          onPublished={() => {
            // Re-fetch job pra mostrar o badge "Publicada"
            refreshAll();
          }}
        />
      )}

      {/* Despublicar do LinkedIn — limita-se a limpar local (Unipile não tem delete remoto) */}
      <AlertDialog open={unpublishDialogOpen} onOpenChange={setUnpublishDialogOpen}>
        <AlertDialogContent data-testid="unpublish-linkedin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-rose-600" />
              Remover publicação do LinkedIn?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded p-3">
                ⚠️ <strong>Importante:</strong> a API da Unipile ainda não permite
                apagar posts do LinkedIn automaticamente. Por isso o fluxo aqui é
                em <strong>2 passos manuais</strong>:
              </span>
              <ol className="list-decimal pl-5 text-sm space-y-1 text-slate-700">
                <li>
                  Clique em <strong>"Abrir post no LinkedIn"</strong> abaixo e apague
                  o post lá (botão "..." → "Excluir post" no LinkedIn).
                </li>
                <li>
                  Depois clique em <strong>"Marcar como removido aqui"</strong> pra
                  o GridRecruiter parar de exibir como "Publicada".
                </li>
              </ol>
              <span className="block text-slate-600 text-sm pt-1">
                Os <strong>interessados</strong> que comentaram/reagiram permanecem
                no painel "Interessados via LinkedIn".
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2 sm:justify-between">
            {job?.linkedin_post_url && (
              <a
                href={job.linkedin_post_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-post-to-delete-button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-2 rounded-md transition-colors"
              >
                <Linkedin className="w-4 h-4" /> Abrir post no LinkedIn
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <div className="flex gap-2">
              <AlertDialogCancel disabled={unpublishBusy}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleUnpublishLinkedIn()}
                disabled={unpublishBusy}
                data-testid="unpublish-confirm-button"
                className="bg-rose-600 hover:bg-rose-700"
              >
                {unpublishBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-1.5" />
                )}
                Marcar como removido aqui
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="confirm-delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta vaga?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os candidatos associados também serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-job">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-job"
              onClick={handleDeleteJob}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir vaga
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDeleteAllCandidates}
        onOpenChange={setConfirmDeleteAllCandidates}
      >
        <AlertDialogContent data-testid="confirm-delete-all-candidates-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Excluir todos os {candidates.length} candidato(s)?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os candidatos analisados pela IA
              serão removidos permanentemente desta vaga.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-all-candidates">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-all-candidates"
              onClick={handleDeleteAllCandidates}
              disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Excluir todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobDetailPage;
